import React, { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PlusCircleIcon, Download, ExternalLink, Edit, Trash2 } from 'lucide-react';
import { Product } from '@/polymet/types/supplier';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getProductDetailUrl } from "@/lib/navigation-helpers";

interface ProductsTableProps {
  products: Product[];
  loading?: boolean;
  onAddProduct: () => void;
  onDeleteProduct: (productId: string) => Promise<void>;
  onRefresh?: () => void;
  className?: string;
}

interface ProductRowProps {
  product: Product;
  onDelete: (productId: string) => Promise<void>;
  deleting?: boolean;
}

const ProductImageCell = memo<{ product: Product }>(({ product }) => {
  const [imageError, setImageError] = useState(false);
  const imageUrl = product.main_image || (product.images && product.images[0]);

  if (!imageUrl || imageError) {
    return (
      <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
        <span className="text-xs text-muted-foreground">No Image</span>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={`${product.name} product image`}
      className="w-12 h-12 object-cover rounded-lg"
      onError={() => setImageError(true)}
      loading="lazy"
    />
  );
});

ProductImageCell.displayName = 'ProductImageCell';

const ProductRow = memo<ProductRowProps>(({ product, onDelete, deleting = false }) => {
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDelete = async () => {
    try {
      setDeleteError(null);
      await onDelete(product.id);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Delete failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete product';
      setDeleteError(errorMessage);
      // Keep dialog open to show error
    }
  };

  const handleViewProduct = () => {
    if (product.slug) {
      window.open(getProductDetailUrl(product.slug), '_blank');
    }
  };

  const handleEditProduct = () => {
    navigate(`/supplier/products/edit/${product.id}`);
  };

  return (
    <>
      <tr className="border-b hover:bg-muted/25 transition-colors">
        <td className="p-4">
          <div className="flex items-center gap-3">
            <ProductImageCell product={product} />
            <div className="min-w-0 flex-1">
              <p className="font-medium truncate" title={product.name}>
                {product.name}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                {product.manufacturer && product.model
                  ? `${product.manufacturer} ${product.model}`
                  : product.manufacturer || product.model || 'N/A'}
              </p>
            </div>
          </div>
        </td>
        <td className="p-4">
          <span className="text-sm">{product.category_name || 'Uncategorized'}</span>
        </td>
        <td className="p-4">
          <Badge variant={product.condition === 'New' ? 'default' : 'secondary'}>
            {product.condition || 'N/A'}
          </Badge>
        </td>
        <td className="p-4">
          <span className="font-medium">
            ₹{product.price?.toLocaleString('en-IN') || '0'}
          </span>
        </td>
        <td className="p-4">
          <div className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${
                product.in_stock ? 'bg-green-500' : 'bg-red-500'
              }`}
              aria-label={product.in_stock ? 'In stock' : 'Out of stock'}
            />
            <span className="text-sm">
              {product.in_stock ? 'In Stock' : 'Out of Stock'}
            </span>
          </div>
        </td>
        <td className="p-4">
          <Badge variant={product.featured ? 'default' : 'outline'}>
            {product.featured ? 'Featured' : 'Standard'}
          </Badge>
        </td>
        <td className="p-4">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleViewProduct}
              disabled={!product.slug}
              aria-label={`View ${product.name} product page`}
            >
              <ExternalLink className="h-4 w-4" />
              <span className="sr-only">View</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleEditProduct}
              aria-label={`Edit ${product.name}`}
            >
              <Edit className="h-4 w-4" />
              <span className="sr-only">Edit</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
              disabled={deleting}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              aria-label={`Delete ${product.name}`}
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only">Delete</span>
            </Button>
          </div>
        </td>
      </tr>

      <AlertDialog open={showDeleteDialog} onOpenChange={(open) => {
        setShowDeleteDialog(open);
        if (!open) setDeleteError(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{product.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {deleteError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
              <strong>Error:</strong> {deleteError}
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});

ProductRow.displayName = 'ProductRow';

const EmptyState = memo<{ onAddProduct: () => void }>(({ onAddProduct }) => (
  <div className="text-center py-12">
    <div className="mx-auto max-w-sm">
      <div className="bg-muted rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
        <PlusCircleIcon className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold mb-2">No products yet</h3>
      <p className="text-muted-foreground mb-6">
        Start by adding your first product to showcase your inventory to potential buyers.
      </p>
      <Button onClick={onAddProduct} size="lg">
        <PlusCircleIcon className="h-4 w-4 mr-2" />
        Add Your First Product
      </Button>
    </div>
  </div>
));

EmptyState.displayName = 'EmptyState';

const LoadingTableRow = memo(() => (
  <tr className="border-b">
    <td className="p-4">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-muted rounded-lg animate-pulse" />
        <div className="space-y-2">
          <div className="h-4 w-32 bg-muted rounded animate-pulse" />
          <div className="h-3 w-24 bg-muted rounded animate-pulse" />
        </div>
      </div>
    </td>
    <td className="p-4">
      <div className="h-4 w-20 bg-muted rounded animate-pulse" />
    </td>
    <td className="p-4">
      <div className="h-6 w-16 bg-muted rounded animate-pulse" />
    </td>
    <td className="p-4">
      <div className="h-4 w-20 bg-muted rounded animate-pulse" />
    </td>
    <td className="p-4">
      <div className="h-4 w-20 bg-muted rounded animate-pulse" />
    </td>
    <td className="p-4">
      <div className="h-6 w-16 bg-muted rounded animate-pulse" />
    </td>
    <td className="p-4">
      <div className="flex gap-1">
        <div className="h-8 w-8 bg-muted rounded animate-pulse" />
        <div className="h-8 w-8 bg-muted rounded animate-pulse" />
        <div className="h-8 w-8 bg-muted rounded animate-pulse" />
      </div>
    </td>
  </tr>
));

LoadingTableRow.displayName = 'LoadingTableRow';

const ProductsTable = memo<ProductsTableProps>(({
  products,
  loading = false,
  onAddProduct,
  onDeleteProduct,
  onRefresh,
  className = ''
}) => {
  if (loading && products.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>My Products</CardTitle>
              <CardDescription>
                Manage your product listings, track performance, and update inventory
              </CardDescription>
            </div>
            <Button onClick={onAddProduct} disabled={loading}>
              <PlusCircleIcon className="h-4 w-4 mr-2" />
              Add New Product
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" role="table">
                <thead className="border-b bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Product</th>
                    <th className="text-left p-4 font-medium">Category</th>
                    <th className="text-left p-4 font-medium">Condition</th>
                    <th className="text-left p-4 font-medium">Price</th>
                    <th className="text-left p-4 font-medium">Stock</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <LoadingTableRow key={index} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (products.length === 0 && !loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>My Products</CardTitle>
              <CardDescription>
                Manage your product listings, track performance, and update inventory
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <EmptyState onAddProduct={onAddProduct} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>My Products</CardTitle>
            <CardDescription>
              Manage your product listings, track performance, and update inventory
            </CardDescription>
          </div>
          <Button onClick={onAddProduct}>
            <PlusCircleIcon className="h-4 w-4 mr-2" />
            Add New Product
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {products.length} product{products.length === 1 ? '' : 's'} listed
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              {onRefresh && (
                <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
                  Refresh
                </Button>
              )}
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" role="table">
                <thead className="border-b bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium" scope="col">Product</th>
                    <th className="text-left p-4 font-medium" scope="col">Category</th>
                    <th className="text-left p-4 font-medium" scope="col">Condition</th>
                    <th className="text-left p-4 font-medium" scope="col">Price</th>
                    <th className="text-left p-4 font-medium" scope="col">Stock</th>
                    <th className="text-left p-4 font-medium" scope="col">Status</th>
                    <th className="text-left p-4 font-medium" scope="col">
                      <span className="sr-only">Actions</span>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <ProductRow
                      key={product.id}
                      product={product}
                      onDelete={onDeleteProduct}
                      deleting={loading}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

ProductsTable.displayName = 'ProductsTable';

export default ProductsTable; 